import { Coins, Filter, Share2, Sparkles, type LucideIcon } from 'lucide-react'

export type ChangelogTag = 'New' | 'Improved' | 'Fixed'

export interface ChangelogEntry {
  /**
   * Monotonically increasing id. The "what's new" announcement re-triggers
   * whenever the highest id here exceeds the id this device last saw, so bump
   * this (don't reuse) for every release you want to surface.
   */
  id: number
  date: string // ISO date, e.g. '2026-06-08'
  tag: ChangelogTag
  title: string
  summary: string
  details?: string[]
  icon?: LucideIcon
}

// Newest first. Keep the copy user-facing — this is the only source both the
// /changelog page and the announcement modal read from.
export const CHANGELOG: ChangelogEntry[] = [
  {
    id: 4,
    date: '2026-07-09',
    tag: 'New',
    title: 'Summarize any YouTube link',
    summary: 'Paste any YouTube URL to get a summary on demand — no need to follow the channel.',
    icon: Sparkles,
    details: [
      'Drop a link into the box at the top of your Feed and hit Summarize.',
      'Works for any video, even from channels you don’t follow — it’s saved to your Library.',
      'Counts toward your monthly summaries, like any other, and uses Rundown Credits once your plan allowance runs out.',
    ],
  },
  {
    id: 3,
    date: '2026-07-01',
    tag: 'New',
    title: 'Share a summary as a tweet',
    summary: 'Turn any summary into a ready-to-post tweet — with a link back — in one click.',
    icon: Share2,
    details: [
      'Open any summary → Share → “Share as tweet”.',
      'We draft the sharpest takeaway as a post; edit it to your voice before sharing.',
      'A link back to the full summary is added automatically.',
    ],
  },
  {
    id: 2,
    date: '2026-06-08',
    tag: 'New',
    title: 'Rundown Credits',
    summary: 'Hit your monthly limit? Top up with credits — no need to change plans.',
    icon: Coins,
    details: [
      '1 credit = 1 summary, used automatically once your plan allowance runs out.',
      'Buy any amount from 100 credits upward.',
      'Unused credits roll over to your next billing cycle — they never expire.',
    ],
  },
  {
    id: 1,
    date: '2026-06-08',
    tag: 'New',
    title: 'Minimum video length filter',
    summary: 'Stop YouTube Shorts from eating your summary quota.',
    icon: Filter,
    details: [
      'Set a minimum length in Settings (default 3 minutes).',
      'Videos shorter than your threshold are skipped — they won’t be summarized or count toward your limit.',
      'Frees up your quota for the long-form videos you actually want.',
    ],
  },
]

export const LATEST_CHANGELOG_ID = CHANGELOG.reduce((max, e) => Math.max(max, e.id), 0)

export function formatChangelogDate(iso: string): string {
  // Parse as UTC to avoid off-by-one from local timezone on date-only strings.
  const d = new Date(`${iso}T00:00:00Z`)
  return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })
}
